/**
 * Guessing a carrier from a tracking number.
 *
 * Nobody should have to pick a courier from a dropdown when the number itself
 * usually says which one it is. These patterns are the widely documented
 * formats, matched only when they are distinctive:
 *
 *   UPS      1Z followed by 16 alphanumerics — unmistakable.
 *   FedEx    12 or 15 digits.
 *   USPS     20 or 22 digits, and the 9-prefixed 22-digit IMpb.
 *   DHL      10 or 11 digits.
 *
 * FedEx and DHL both use bare digits, so length is doing the work and a
 * mistake is possible. That is why an unmatched or ambiguous number renders as
 * PLAIN TEXT rather than a link: sending someone to the wrong courier's site
 * is worse than making them paste it themselves, and the number is still
 * copyable either way.
 */

export interface CarrierMatch {
  carrier: string;
  url: string;
}

/** Digits and letters only: suppliers paste numbers with spaces and dashes. */
export function normalizeTracking(raw: string): string {
  return raw.replace(/[\s-]/g, '').toUpperCase();
}

export function detectCarrier(raw: string): CarrierMatch | null {
  const value = normalizeTracking(raw);
  if (value === '') {
    return null;
  }

  if (/^1Z[0-9A-Z]{16}$/.test(value)) {
    return {
      carrier: 'UPS',
      url: `https://www.ups.com/track?tracknum=${value}`,
    };
  }

  // The 9-prefixed 22-digit form is USPS IMpb and is checked before the
  // generic digit rules, which would otherwise not reach it.
  if (/^9\d{21}$/.test(value) || /^\d{20}$/.test(value)) {
    return {
      carrier: 'USPS',
      url: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${value}`,
    };
  }

  if (/^\d{12}$/.test(value) || /^\d{15}$/.test(value)) {
    return {
      carrier: 'FedEx',
      url: `https://www.fedex.com/fedextrack/?trknbr=${value}`,
    };
  }

  if (/^\d{10,11}$/.test(value)) {
    return {
      carrier: 'DHL',
      url: `https://www.dhl.com/en/express/tracking.html?AWB=${value}`,
    };
  }

  return null;
}
